import DeleteIcon from "@shared/svg/delete-document.svg?react";
import { AlertBox } from "@shared/ui/AlertBox";
import { BUTTON_VARIANTS } from "@shared/ui/Button";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useIntl } from "react-intl";
import usePopper from "react-overlays/usePopper";
import { toast } from "react-toastify";

import { useAuth } from "~/components/general";
import PreviewEvent from "~/components/preventiveMaintenance/EventPreview";
import MainTile from "~/components/preventiveMaintenance/MainTile";
import OccurrenceSchedule from "~/components/preventiveMaintenance/OccurrenceSchedule";
import { deletePreventiveMaintenanceEvent } from "~/services/preventiveMaintenance";

const PreventiveEvent = ({
  eventId,
  title,
  startDate,
  repeatIn,
  repeatInNumber,
  eventDate,
  description,
  frequency,
  ticketCreationNumber,
  ticketCreationIn,
  estimatedDurationNumber,
  estimatedDurationIn,
  repetitionEnd,
  repetitionEndValueDate,
  repetitionEndValueOccurrences,
  endsOn,
  lastCompletedAt,
  allOccurrencesDone,
  nextCreationDate,
  onEditButtonClick,
  machineId,
  machineName,
  createdBy,
  refetchEvents,
  tileVariant = false,
  procedures = 0,
  inventoryParts = 0,
  machineFacilityName = "",
  machineFacilityId = "",
  isSharedAssetsPage = false,
  occurrenceSchedule = null,
  isExpanded = false,
  onToggleExpand = () => {},
}) => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);

  const { user } = useAuth();

  const dateFormat = user.dateFormat || "DD-MM-YYYY";
  const timeFormat = user.timeFormat || "HH:mm";

  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // TODO: add machine ID
  const { deleteEvent } = deletePreventiveMaintenanceEvent(machineId);

  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      placement: "bottom-end",
      strategy: "fixed",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
        {
          name: "flip",
          options: {
            fallbackPlacements: ["top-end", "bottom-start", "top-start"],
            padding: 8,
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: 8,
            boundary: "viewport",
            altAxis: true,
          },
        },
      ],
    },
  );

  // Reposition on open — list may have shifted after deletes / accordion expands.
  useEffect(() => {
    if (!showPreview) return;
    const frame = requestAnimationFrame(() => {
      update?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [showPreview, update, referenceElement, popperElement]);

  const {
    messages: { preventiveMaintenance: PM, common },
  } = useIntl();

  const handleEventDelete = async () => {
    return setShowDeleteModal(true);
  };

  const handleRemoveEventAcceptClick = async () => {
    try {
      await deleteEvent(eventId);
      setShowPreview(false);
      refetchEvents();
      toast(
        <Toast type={TOAST_TYPES.SUCCESS} message={PM?.successDeletion} />,
        {
          closeButton: false,
        },
      );
      return setShowDeleteModal(false);
    } catch (exception) {
      if (exception?.message === "Invalid Id") {
        setShowDeleteModal(false);
        return toast(
          <Toast type={TOAST_TYPES.ERROR} message={PM?.error?.eventNotFound} />,
          {
            closeButton: false,
          },
        );
      }
      toast(
        <Toast
          type={TOAST_TYPES.ERROR}
          message={common?.errors?.somethingWrong}
        />,
        {
          closeButton: false,
        },
      );
    }
  };

  const handleRemoveEventCancelClick = () => {
    setShowDeleteModal(false);
  };

  const handleEventEdit = () => {
    const rin = repeatInNumber === 0 ? "" : repeatInNumber;
    const tcn = ticketCreationNumber === 0 ? "" : ticketCreationNumber;
    const edn =
      estimatedDurationNumber === 0 || estimatedDurationNumber == null
        ? "1"
        : estimatedDurationNumber;

    onEditButtonClick({
      _id: eventId,
      title,
      repeatIn,
      repeatInNumber: rin?.toString() || "",
      eventDate,
      // Bulk-imported events have no description (null from the API)
      description: description ?? "",
      frequency,
      ticketCreationNumber: tcn?.toString() || "",
      ticketCreationIn,
      estimatedDurationNumber: edn?.toString() || "1",
      estimatedDurationIn: estimatedDurationIn || "Day",
      repetitionEnd: repetitionEnd || "never",
      repetitionEndValueDate: repetitionEndValueDate || "",
      repetitionEndValueOccurrences: repetitionEndValueOccurrences || "",
      machine: machineId,
      procedures,
      inventoryParts,
    });
    return setShowPreview(false);
  };

  const handleEventPreview = () => {
    setShowPreview(true);
  };
  const handlePreviewOutsideClick = () => {
    setShowPreview(false);
  };

  return (
    <>
      <MainTile
        tileVariant={tileVariant}
        title={title}
        machineName={machineName || ""}
        eventId={eventId}
        eventDate={eventDate}
        endsOn={endsOn}
        lastCompletedAt={lastCompletedAt}
        allOccurrencesDone={allOccurrencesDone}
        handleEventPreview={handleEventPreview}
        containerRef={setReferenceElement}
        repeatInNumber={repeatInNumber}
        repeatIn={repeatIn}
        frequency={frequency}
        createdBy={createdBy}
        machineFacilityName={machineFacilityName}
        machineId={machineId}
        machineFacilityId={machineFacilityId}
        dateFormat={dateFormat}
        isSharedAssetsPage={isSharedAssetsPage}
        showAccordion={!tileVariant}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        scheduleContent={
          <OccurrenceSchedule
            eventId={eventId}
            occurrenceSchedule={occurrenceSchedule}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            refetchEvents={refetchEvents}
            isSharedAssetsPage={isSharedAssetsPage}
          />
        }
      />
      {createPortal(
        <div
          ref={setPopperElement}
          style={{
            visibility: showPreview ? "visible" : "hidden",
            pointerEvents: showPreview ? "auto" : "none",
            zIndex: 50,
            ...styles.popper,
          }}
          {...attributes.popper}
        >
          {showPreview && (
            <PreviewEvent
              isVisible={showPreview}
              eventId={eventId}
              onOutsideClick={handlePreviewOutsideClick}
              title={title}
              repeatIn={repeatIn}
              repeatInNumber={repeatInNumber}
              eventDate={eventDate}
              description={description}
              frequency={frequency}
              nextCreationDate={nextCreationDate}
              onDeleteButtonClick={handleEventDelete}
              onEditButtonClick={handleEventEdit}
              ticketCreationIn={ticketCreationIn}
              ticketCreationNumber={ticketCreationNumber}
              createdBy={createdBy}
              procedureCount={procedures?.length || 0}
              inventoryPartsCount={
                inventoryParts?.filter((part) => part?.quantity > 0).length || 0
              }
              tileVariant={tileVariant}
              machineName={machineName}
              dateFormat={dateFormat}
              endsOn={endsOn}
              lastCompletedAt={lastCompletedAt}
              allOccurrencesDone={allOccurrencesDone}
            />
          )}
        </div>,
        document.body,
      )}
      <AlertBox
        title={PM?.deleteModalTitle}
        description={PM?.deleteModalDescription}
        isOpen={showDeleteModal}
        acceptButtonText={PM?.deleteModalAcceptBtnText}
        acceptButtonVariant={BUTTON_VARIANTS.DANGER}
        cancelButtonText={PM?.deleteModalCancelBtnText}
        image={<DeleteIcon width="130" height="130" />}
        onAccept={handleRemoveEventAcceptClick}
        onCancel={handleRemoveEventCancelClick}
        overlay={true}
        // Preview popover is portaled at z-50; keep the confirm dialog above it.
        zIndexClass="z-[60]"
      />
    </>
  );
};

export default PreventiveEvent;
